import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  ChevronDown,
  ChevronUp,
  Clock,
  User,
  MapPin,
  Package,
  Truck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ScanLine,
  FileText } from
'lucide-react';

// Vertical Timeline (Uber-style)
export const VerticalTimeline = ({ events, onEventClick }) => {const { t } = useTranslation("wms");
  const getEventIcon = (event) => {
    const type = event.event_type || event.type;
    if (type?.includes('SCAN')) return ScanLine;
    if (type?.includes('DELIVERED') || type?.includes('COMPLETED')) return CheckCircle2;
    if (type?.includes('FAILED') || type?.includes('REJECTED')) return XCircle;
    if (type?.includes('ROUTE') || type?.includes('STOP')) return Truck;
    if (type?.includes('PACKAGE') || type?.includes('ORDER')) return Package;
    return Clock;
  };

  const getEventColor = (event) => {
    const type = event.event_type || event.type;
    const status = event.status || event.scan_result;
    if (status === 'REJECTED' || status === 'FAILED' || type?.includes('FAILED')) return 'bg-red-500';
    if (status === 'WARNING') return 'bg-amber-500';
    if (status === 'OK' || type?.includes('DELIVERED') || type?.includes('COMPLETED')) return 'bg-green-500';
    if (type?.includes('STARTED') || type?.includes('ARRIVED')) return 'bg-blue-500';
    return 'bg-gray-400';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative" data-testid="vertical-timeline">
      {/* Timeline Line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
      
      {events.map((event, index) => {
        const Icon = getEventIcon(event);
        const colorClass = getEventColor(event);

        return (
          <div
            key={event.id || index}
            className="relative pl-12 pb-8 last:pb-0"
            onClick={() => onEventClick?.(event)}>
            
            {/* Timeline Dot */}
            <div className={`absolute left-2 w-5 h-5 rounded-full ${colorClass} flex items-center justify-center ring-4 ring-background`}>
              <Icon className="h-3 w-3 text-white" />
            </div>
            
            {/* Event Card */}
            <Card className="cursor-pointer hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm">
                        {event.event_type || event.type || 'Event'}
                      </h4>
                      {event.scan_result &&
                      <Badge variant={event.scan_result === 'OK' ? 'default' : 'destructive'}>
                          {event.scan_result}
                        </Badge>
                      }
                    </div>
                    {event.payload?.message || event.message ?
                    <p className="text-sm text-muted-foreground mt-1">
                        {event.payload?.message || event.message}
                      </p> :
                    null}
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {event.actor_id &&
                      <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {event.actor_id}
                        </span>
                      }
                      {event.entity_id &&
                      <span className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {event.entity_id}
                        </span>
                      }
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>{formatTime(event.timestamp || event.timestamp_server)}</p>
                    <p>{formatDate(event.timestamp || event.timestamp_server)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>);

      })}
      
      {events.length === 0 &&
      <div className="text-center py-8 text-muted-foreground">{t("noEventsTo", "No events to display")}

      </div>
      }
    </div>);

};

// Horizontal Progress Timeline
export const HorizontalTimeline = ({ events, onEventClick }) => {const { t } = useTranslation("wms");
  const getEventColor = (event) => {
    const type = event.event_type || event.type;
    const status = event.status || event.scan_result;
    if (status === 'REJECTED' || status === 'FAILED' || type?.includes('FAILED')) return 'bg-red-500';
    if (status === 'WARNING') return 'bg-amber-500';
    if (status === 'OK' || type?.includes('DELIVERED') || type?.includes('COMPLETED')) return 'bg-green-500';
    if (type?.includes('STARTED') || type?.includes('ARRIVED')) return 'bg-blue-500';
    return 'bg-gray-300';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Find the current/active step (last non-future event)
  const activeIndex = events.length - 1;

  return (
    <div className="relative" data-testid="horizontal-timeline">
      {/* Progress Bar Container */}
      <div className="flex items-center">
        {events.map((event, index) => {
          const isActive = index <= activeIndex;
          const isLast = index === events.length - 1;
          const colorClass = isActive ? getEventColor(event) : 'bg-gray-200';

          return (
            <React.Fragment key={event.id || index}>
              {/* Milestone Dot */}
              <div
                className="flex flex-col items-center cursor-pointer group"
                onClick={() => onEventClick?.(event)}>
                
                <div className={`w-4 h-4 rounded-full ${colorClass} ring-2 ring-background group-hover:ring-primary transition-all`} />
                <div className="mt-2 text-center max-w-20">
                  <p className="text-xs font-medium truncate">
                    {(event.event_type || event.type || '').split('.').pop()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTime(event.timestamp || event.timestamp_server)}
                  </p>
                </div>
              </div>
              
              {/* Connector Line */}
              {!isLast &&
              <div className={`flex-1 h-1 mx-1 ${isActive ? getEventColor(events[index + 1]) : 'bg-gray-200'}`} />
              }
            </React.Fragment>);

        })}
      </div>
      
      {events.length === 0 &&
      <div className="text-center py-8 text-muted-foreground">{t("noEventsTo", "No events to display")}

      </div>
      }
    </div>);

};

// Combined Timeline Component with Toggle
const TrailTimeline = ({
  events,
  onEventClick,
  defaultView = 'vertical',
  showViewToggle = true
}) => {const { t } = useTranslation("wms");
  const [viewType, setViewType] = useState(defaultView);

  return (
    <div>
      {showViewToggle &&
      <div className="flex justify-end mb-4">
          <div className="inline-flex rounded-lg border p-1">
            <Button
            variant={viewType === 'vertical' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewType('vertical')}
            className="text-xs">{t("vertical", "Vertical")}


          </Button>
            <Button
            variant={viewType === 'horizontal' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewType('horizontal')}
            className="text-xs">{t("horizontal", "Horizontal")}


          </Button>
          </div>
        </div>
      }
      
      {viewType === 'vertical' ?
      <VerticalTimeline events={events} onEventClick={onEventClick} /> :

      <HorizontalTimeline events={events} onEventClick={onEventClick} />
      }
    </div>);

};

export default TrailTimeline;